import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  type SupportedLocale,
} from "@/i18n/locales";

const CHAT_APP_ERROR_CODES = [
  "AI_MODEL_ACCESS_UNRESOLVED",
  "AI_PLAN_REQUIRED",
  "AI_MODEL_RESTRICTED",
  "AI_MODEL_ATTACHMENT_UNSUPPORTED",
  "AI_QUOTA_STATUS_UNAVAILABLE",
  "AI_QUOTA_EXCEEDED",
] as const;

export type ChatAppErrorCode = (typeof CHAT_APP_ERROR_CODES)[number];

export type ChatAppErrorResponse = {
  code: ChatAppErrorCode;
  message: string;
};

const chatAppErrorCodeSet = new Set<string>(CHAT_APP_ERROR_CODES);

export const DEFAULT_CHAT_RESPONSE_ERROR_MESSAGE =
  "Failed to fetch the chat response.";

function isChatAppErrorCode(value: unknown): value is ChatAppErrorCode {
  return typeof value === "string" && chatAppErrorCodeSet.has(value);
}

function isChatAppErrorResponse(value: unknown): value is ChatAppErrorResponse {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    isChatAppErrorCode(candidate.code) && typeof candidate.message === "string"
  );
}

export function resolveChatAppErrorLocale(
  lang: string | null | undefined,
): SupportedLocale {
  return isSupportedLocale(lang) ? lang : DEFAULT_LOCALE;
}

export class ChatAppError extends Error {
  readonly code: ChatAppErrorCode;

  constructor(response: ChatAppErrorResponse) {
    super(response.message);
    this.name = "ChatAppError";
    this.code = response.code;
  }

  static isInstance(error: unknown): error is ChatAppError {
    return error instanceof ChatAppError;
  }
}

/**
 * DefaultChatTransport は non-OK response を plain text Error に潰すため、
 * app-level error envelope を先に解釈して custom Error へ変換する。
 */
export async function createChatTransportError(
  response: Response,
): Promise<Error> {
  const responseText = await response.text().catch(() => "");
  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    try {
      const parsed = JSON.parse(responseText) as unknown;
      if (isChatAppErrorResponse(parsed)) {
        return new ChatAppError(parsed);
      }
    } catch {
      // Fall through to plain-text fallback below.
    }
  }

  return new Error(responseText || DEFAULT_CHAT_RESPONSE_ERROR_MESSAGE);
}
